import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Shield, Users, Bell, CreditCard, Globe, Database, Key } from 'lucide-react';
import { adminService } from '../services/adminService';
import toast from 'react-hot-toast';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState({
    general: {
      siteName: 'Pair Pinnacle',
      siteDescription: 'Influencer Marketing Platform',
      contactEmail: 'admin@pairpinnacle.com',
      timezone: 'UTC',
      dateFormat: 'MM/DD/YYYY',
      currency: 'USD',
    },
    security: {
      sessionTimeout: 30,
      maxLoginAttempts: 5,
      requireTwoFactor: false,
      passwordMinLength: 8,
      enableAuditLog: true,
    },
    notifications: {
      emailNotifications: true,
      pushNotifications: true,
      smsNotifications: false,
      notificationFrequency: 'immediate',
    },
    payment: {
      stripeEnabled: true,
      paypalEnabled: true,
      minimumWithdrawal: 50,
      transactionFee: 2.9,
      currencyConversion: true,
    },
    features: {
      enableCampaigns: true,
      enableInfluencerDiscovery: true,
      enableAnalytics: true,
      enableChat: true,
      enableReviews: true,
    }
  });

  // Fetch settings on component mount
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await adminService.getSettings();
      
      if (data && typeof data === 'object') {
        setSettings(prev => ({ ...prev, ...data }));
        setError(null);
        console.log('Settings loaded:', data);
      } else {
        console.error('Unexpected data format:', data);
        setError('Invalid data format received from server');
        toast.error('Invalid data format received');
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError(err.message);
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  const handleSaveSettings = async (section) => {
    try {
      setSaving(true);
      const sectionSettings = settings[section];
      
      // Save each setting individually
      for (const [key, value] of Object.entries(sectionSettings)) {
        await adminService.updateSetting(`${section}.${key}`, value);
      }
      
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} settings saved successfully!`);
    } catch (error) {
      toast.error(`Failed to save ${section} settings`);
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      
      // Save all settings
      for (const [section, sectionSettings] of Object.entries(settings)) {
        for (const [key, value] of Object.entries(sectionSettings)) {
          await adminService.updateSetting(`${section}.${key}`, value);
        }
      }
      
      toast.success('All settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save all settings');
      console.error('Error saving all settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults? This action cannot be undone.')) {
      setSettings({
        general: {
          siteName: 'Pair Pinnacle',
          siteDescription: 'Influencer Marketing Platform',
          contactEmail: 'admin@pairpinnacle.com',
          timezone: 'UTC',
          dateFormat: 'MM/DD/YYYY',
          currency: 'USD',
        },
        security: {
          sessionTimeout: 30,
          maxLoginAttempts: 5,
          requireTwoFactor: false,
          passwordMinLength: 8,
          enableAuditLog: true,
        },
        notifications: {
          emailNotifications: true,
          pushNotifications: true,
          smsNotifications: false,
          notificationFrequency: 'immediate',
        },
        payment: {
          stripeEnabled: true,
          paypalEnabled: true,
          minimumWithdrawal: 50,
          transactionFee: 2.9,
          currencyConversion: true,
        },
        features: {
          enableCampaigns: true,
          enableInfluencerDiscovery: true,
          enableAnalytics: true,
          enableChat: true,
          enableReviews: true,
        }
      });
      toast.success('Settings reset to defaults');
    }
  };

  const SettingSection = ({ title, icon: Icon, children, sectionKey }) => (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary-100">
            <Icon className="h-5 w-5 text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <button
          onClick={() => handleSaveSettings(sectionKey)}
          disabled={saving}
          className="btn-primary flex items-center gap-2"
        >
          {saving ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Save
        </button>
      </div>
      {children}
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600">
            {error ? 'Showing default settings (API connection failed)' : 'Manage platform configuration'}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={resetToDefaults}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Reset to Defaults
          </button>
          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="btn-primary flex items-center gap-2"
          >
            {saving ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save All
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="card p-6 mb-6">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <p className="text-gray-600 mb-4">Showing default settings below. Click retry to attempt fetching real settings again.</p>
            <button onClick={fetchSettings} className="btn-primary">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* General Settings */}
      <SettingSection title="General Settings" icon={Globe} sectionKey="general">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
            <input
              type="text"
              value={settings.general.siteName}
              onChange={(e) => handleInputChange('general', 'siteName', e.target.value)}
              className="input-field w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Site Description</label>
            <input
              type="text"
              value={settings.general.siteDescription}
              onChange={(e) => handleInputChange('general', 'siteDescription', e.target.value)}
              className="input-field w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
            <input
              type="email"
              value={settings.general.contactEmail}
              onChange={(e) => handleInputChange('general', 'contactEmail', e.target.value)}
              className="input-field w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
            <select
              value={settings.general.timezone}
              onChange={(e) => handleInputChange('general', 'timezone', e.target.value)}
              className="input-field w-full"
            >
              <option value="UTC">UTC</option>
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
            <select
              value={settings.general.dateFormat}
              onChange={(e) => handleInputChange('general', 'dateFormat', e.target.value)}
              className="input-field w-full"
            >
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
            <select
              value={settings.general.currency}
              onChange={(e) => handleInputChange('general', 'currency', e.target.value)}
              className="input-field w-full"
            >
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
              <option value="INR">INR (₹)</option>
            </select>
          </div>
        </div>
      </SettingSection>

      {/* Security Settings */}
      <SettingSection title="Security Settings" icon={Shield} sectionKey="security">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
            <input
              type="number"
              min="5"
              max="480"
              value={settings.security.sessionTimeout}
              onChange={(e) => handleInputChange('security', 'sessionTimeout', parseInt(e.target.value))}
              className="input-field w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Max Login Attempts</label>
            <input
              type="number"
              min="3"
              max="10"
              value={settings.security.maxLoginAttempts}
              onChange={(e) => handleInputChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
              className="input-field w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password Min Length</label>
            <input
              type="number"
              min="6"
              max="20"
              value={settings.security.passwordMinLength}
              onChange={(e) => handleInputChange('security', 'passwordMinLength', parseInt(e.target.value))}
              className="input-field w-full"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="requireTwoFactor"
              checked={settings.security.requireTwoFactor}
              onChange={(e) => handleInputChange('security', 'requireTwoFactor', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="requireTwoFactor" className="ml-2 block text-sm text-gray-900">
              Require Two-Factor Authentication
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableAuditLog"
              checked={settings.security.enableAuditLog}
              onChange={(e) => handleInputChange('security', 'enableAuditLog', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="enableAuditLog" className="ml-2 block text-sm text-gray-900">
              Enable Audit Logging
            </label>
          </div>
        </div>
      </SettingSection>

      {/* Notification Settings */}
      <SettingSection title="Notification Settings" icon={Bell} sectionKey="notifications">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="emailNotifications"
              checked={settings.notifications.emailNotifications}
              onChange={(e) => handleInputChange('notifications', 'emailNotifications', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="emailNotifications" className="ml-2 block text-sm text-gray-900">
              Enable Email Notifications
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="pushNotifications"
              checked={settings.notifications.pushNotifications}
              onChange={(e) => handleInputChange('notifications', 'pushNotifications', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="pushNotifications" className="ml-2 block text-sm text-gray-900">
              Enable Push Notifications
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="smsNotifications"
              checked={settings.notifications.smsNotifications}
              onChange={(e) => handleInputChange('notifications', 'smsNotifications', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="smsNotifications" className="ml-2 block text-sm text-gray-900">
              Enable SMS Notifications
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notification Frequency</label>
            <select
              value={settings.notifications.notificationFrequency}
              onChange={(e) => handleInputChange('notifications', 'notificationFrequency', e.target.value)}
              className="input-field w-full"
            >
              <option value="immediate">Immediate</option>
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
        </div>
      </SettingSection>

      {/* Payment Settings */}
      <SettingSection title="Payment Settings" icon={CreditCard} sectionKey="payment">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="stripeEnabled"
              checked={settings.payment.stripeEnabled}
              onChange={(e) => handleInputChange('payment', 'stripeEnabled', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="stripeEnabled" className="ml-2 block text-sm text-gray-900">
              Enable Stripe Payments
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="paypalEnabled"
              checked={settings.payment.paypalEnabled}
              onChange={(e) => handleInputChange('payment', 'paypalEnabled', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="paypalEnabled" className="ml-2 block text-sm text-gray-900">
              Enable PayPal Payments
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Withdrawal Amount</label>
            <input
              type="number"
              min="1"
              step="0.01"
              value={settings.payment.minimumWithdrawal}
              onChange={(e) => handleInputChange('payment', 'minimumWithdrawal', parseFloat(e.target.value))}
              className="input-field w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Fee (%)</label>
            <input
              type="number"
              min="0"
              max="10"
              step="0.1"
              value={settings.payment.transactionFee}
              onChange={(e) => handleInputChange('payment', 'transactionFee', parseFloat(e.target.value))}
              className="input-field w-full"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="currencyConversion"
              checked={settings.payment.currencyConversion}
              onChange={(e) => handleInputChange('payment', 'currencyConversion', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="currencyConversion" className="ml-2 block text-sm text-gray-900">
              Enable Currency Conversion
            </label>
          </div>
        </div>
      </SettingSection>

      {/* Feature Settings */}
      <SettingSection title="Feature Settings" icon={Key} sectionKey="features">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableCampaigns"
              checked={settings.features.enableCampaigns}
              onChange={(e) => handleInputChange('features', 'enableCampaigns', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="enableCampaigns" className="ml-2 block text-sm text-gray-900">
              Enable Campaign Management
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableInfluencerDiscovery"
              checked={settings.features.enableInfluencerDiscovery}
              onChange={(e) => handleInputChange('features', 'enableInfluencerDiscovery', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="enableInfluencerDiscovery" className="ml-2 block text-sm text-gray-900">
              Enable Influencer Discovery
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableAnalytics"
              checked={settings.features.enableAnalytics}
              onChange={(e) => handleInputChange('features', 'enableAnalytics', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="enableAnalytics" className="ml-2 block text-sm text-gray-900">
              Enable Analytics Dashboard
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableChat"
              checked={settings.features.enableChat}
              onChange={(e) => handleInputChange('features', 'enableChat', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="enableChat" className="ml-2 block text-sm text-gray-900">
              Enable Chat System
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableReviews"
              checked={settings.features.enableReviews}
              onChange={(e) => handleInputChange('features', 'enableReviews', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="enableReviews" className="ml-2 block text-sm text-gray-900">
              Enable Review System
            </label>
          </div>
        </div>
      </SettingSection>
    </div>
  );
};

export default Settings;
